import React, { createContext, useContext, useRef, useCallback } from 'react';
import {
  TraitsDictionary,
  DiceDictionary,
  rightStart,
  leftStart,
  TraitSide,
} from './ConstantsCollection.js';
import { useCharacter } from './CharacterInfo.js';

const CharacterAction = createContext();

export const CharacterActionProvider = ({ children }) => {
  const rollTraitRefs = useRef([]);

  const { character } = useCharacter();

  function getRandomNumber(maxNum) {
    return Math.floor(Math.random() * maxNum) + 1;
  }

  const getDiceInfo = name => {
    var [diceNum, IconComponent] = DiceDictionary[0];

    for (const [, trait] of Object.entries(TraitsDictionary)) {
      if (trait.name === name) {
        if (character.traits[trait.name] % 1 !== 0) break;
        [diceNum, IconComponent] =
          DiceDictionary[
            character.traits[trait.name] < rightStart
              ? rightStart - character.traits[trait.name]
              : character.traits[trait.name] - leftStart
          ];
      } else if (trait.leftSide.name === name) {
        [diceNum, IconComponent] =
          DiceDictionary[
            character.traits[trait.name] < rightStart
              ? rightStart - character.traits[trait.name]
              : 0
          ];
      } else if (trait.rightSide.name === name) {
        [diceNum, IconComponent] =
          DiceDictionary[
            character.traits[trait.name] > leftStart
              ? character.traits[trait.name] - leftStart
              : 0
          ];
      }
    }
    return [diceNum, IconComponent];
  };

  const getDiceIcon = (name, props = {}) => {
    var [diceNum, IconComponent] = DiceDictionary[0];
    var result = getDiceInfo(name);
    if (result)
      // eslint-disable-next-line no-unused-vars
      [diceNum, IconComponent] = result;
    return IconComponent ? <IconComponent {...props} /> : null;
  };

  const getBaseTrait = traitName => {
    for (const key in TraitsDictionary) {
      const trait = TraitsDictionary[key];
      if (trait.name === traitName) return [trait.name, TraitSide.Center];
      if (trait.leftSide.name === traitName)
        return [trait.name, TraitSide.Left];
      if (trait.rightSide.name === traitName)
        return [trait.name, TraitSide.Right];
    }
    console.log('No base trait found for: ' + traitName);
    return null;
  };

  const mapTraitToOption = ({ item, ignoreUsed }) => {
    const [parentTraitName, traitSide] = getBaseTrait(item.name);

    return {
      value: item,
      name: item.name,
      parent: parentTraitName,
      traitSide,
      disabled: character.usedTraits?.includes(item.name),
      label: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color:
              character.usedTraits?.includes(item.name) && !ignoreUsed
                ? 'gray'
                : 'black',
          }}
        >
          <span
            style={{
              textDecoration:
                character.usedTraits?.includes(item.name) && !ignoreUsed
                  ? 'line-through'
                  : 'none',
            }}
          >
            {item.name}
          </span>
          <div style={{ marginLeft: '8px', flex: '0 0 auto' }}>
            {getDiceIcon(item.name, {
              style: {
                strokeWidth: '.5',
                fontSize: '1.5rem',
              },
            })}
          </div>
        </div>
      ),
      shortLabel: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            color: character.usedTraits?.includes(item.name) ? 'grey' : 'black',
          }}
        >
          <span
            style={{
              textDecoration: character.usedTraits?.includes(item.name)
                ? 'line-through'
                : 'none',
            }}
          >
            {item.abbr}
          </span>
          {getDiceIcon(item.name, {
            style: {
              strokeWidth: '.5',
              fontSize: '1rem',
            },
          })}
        </div>
      ),
    };
  };

  const registerRollTrait = useCallback(fn => {
    rollTraitRefs.current.push(fn);
  }, []);

  const unregisterRollTrait = useCallback(fn => {
    rollTraitRefs.current = rollTraitRefs.current.filter(f => f !== fn);
  }, []);

  const triggerRollTrait = trait => {
    const result = getBaseTrait(trait);
    if (result) {
      const [traitName, traitSide] = result;
      const rollNum = getRandomNumber(10);
      const traitValue = character.traits[traitName];

      const isLeft =
        traitSide === TraitSide.Center
          ? traitValue <= 5
          : traitSide === TraitSide.Left;

      const success =
        traitValue === rollNum
          ? true
          : isLeft
            ? traitValue < rollNum
            : traitValue > rollNum;

      rollTraitRefs.current.forEach(fn =>
        fn?.(traitName, traitSide, rollNum, success)
      );
      return success;
    }
    return false;
  };

  const triggerRollDiceTrait = trait => {
    var result = getDiceInfo(trait);
    if (result) {
      var [diceNum] = result;
      return getRandomNumber(diceNum);
    }
    return 0;
  };

  const triggerRollDiceSides = (num, sides) => {
    var total = 0;
    for (let i = 0; i < num; i++) {
      total += getRandomNumber(sides);
    }
    return total;
  };

  const updateTraitsFromParsedTraits = traits => {
    Object.values(TraitsDictionary).forEach(trait => {
      const sections = [trait, trait.leftSide, trait.rightSide];

      sections.forEach(section => {
        if (section?.name === traits.title) {
          section.link = traits.id;
          section.summary = traits.summary;
        }
      });
    });

    traits.subsections?.forEach(sub => updateTraitsFromParsedTraits(sub));
  };

  return (
    <CharacterAction.Provider
      value={{
        getDiceIcon,
        mapTraitToOption,

        registerRollTrait,
        unregisterRollTrait,
        triggerRollTrait,

        triggerRollDiceTrait,
        triggerRollDiceSides,

        updateTraitsFromParsedTraits,
      }}
    >
      {children}
    </CharacterAction.Provider>
  );
};

export const useAction = () => useContext(CharacterAction);
